import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { type CreateOrderDto, LocationType, SaleType, ScanForm } from './useValidateOrder'
import { nanoid } from 'nanoid'
import type { ProductDto } from '@/components/products/types/product.types'
import { getZmpConfig } from '@/common/provider/zmp.config'
import dayjs from 'dayjs'
import { useStoreStore } from '@/components/stores/hooks/useStore'
import { createStorefrontUrl, getStorefrontStorageKey } from '@/common/storefront/storefront.config'


export type CartItemOption = {
  id: string | number
  quantity: number
}

export type CartItemOptions = Record<string | number, CartItemOption[]>

export type CartItem = ProductDto & {
  // Thêm trường itemNo để phân biệt các sản phẩm
  itemNo?: string
  itemNote?: string
  itemQty?: number
  itemSelected?: boolean
  itemOptions?: CartItemOptions
}

const normalizeItemNote = (itemNote?: string) => (itemNote ?? '').trim()

const getItemOptionsSignature = (itemOptions: CartItem['itemOptions']) => {
  if (!itemOptions) return ''

  const optionIds = Object.keys(itemOptions).sort()
  const optionGroups: string[] = []

  for (const optionId of optionIds) {
    const values = Array.isArray(itemOptions[optionId])
      ? itemOptions[optionId]
      : []
    const selectedValues: string[] = []

    for (const value of values) {
      const quantity = value?.quantity ?? 1
      if (quantity <= 0) continue

      selectedValues.push(`${String(value?.id)}:${quantity}`)
    }

    if (selectedValues.length === 0) continue

    selectedValues.sort()
    optionGroups.push(`${optionId}=${selectedValues.join(',')}`)
  }

  return optionGroups.join('|')
}

export const isSameCartItemConfiguration = (
  left: CartItem,
  right: CartItem,
) => (
  left.id === right.id &&
  normalizeItemNote(left.itemNote) === normalizeItemNote(right.itemNote) &&
  getItemOptionsSignature(left.itemOptions) ===
    getItemOptionsSignature(right.itemOptions)
)

export type Store = {
  core: {
    scan_from: any
    location: {
      type: string
    },
    saleType?: SaleType.Delivery | SaleType.DineIn | SaleType.PickUp
    contactName?: string
    contactPhone?: string
    storeId?: number
    deliveryTime?: string
    shippingData: {
      addressLine?: string
      addressType?: string
      stateProvinceRegion?: string
      country?: string
      city?: string
      district?: string
      ward?: string
      street?: string
      longitude?: number
      latitude?: number
      fullAddress?: string
    }
    order_note?: string
    invoice?: {
      hasInvoice?: boolean
      taxCode?: string
      companyName?: string
      address?: string
      email?: string
    },
    /**
     * Mảng chứa mã voucher đã chọn
     */
    voucher_codes: string[]
    order_meta: {
      deviceId?: string
      deviceName?: string
      deviceOs?: string
      deviceIp?: string
      deviceToken?: string
      trainCode?: string
      coachCode?: string
      seatNumber?: string
    }
  }
  products: CartItem[] // Thay đổi thành mảng để lưu nhiều sản phẩm
  updateCore: (core: Partial<Store['core']>) => void
  updateOrderMeta: (payload: Partial<Store['core']['order_meta']>) => void
  addProduct: (product: CartItem) => void
  updateProduct: (product: CartItem) => void
  deleteProduct: (itemNo: CartItem['itemNo']) => void
  formatProduct: (product: CartItem) => CartItem
  getProduct: (itemNo: CartItem['itemNo']) => CartItem | undefined // Thêm hàm getProduct
  setVoucherCode: (voucherCode: string) => void
  setPaymentMethod: (method: string) => void
  initCart: () => void
  /**
   * 
   * @returns {CreateOrderDto} Trả về thông tin đơn hàng
   */
  generateOrder: () => void
  order: CreateOrderDto
  // Áp dụng cho Web Order
  createCheckout: (order: {
    /**
     * Mã đơn hàng
     */
    id: string
    /**
     * Định dạng mã để lấy chi tiết đơn hàng
     * uuid | id | order_number
     */
    id_type: 'uuid' | 'id' | 'order_number'
  }) => void
  checkout: {
    id: string
    id_type: 'uuid' | 'id' | 'order_number'
  }
}

const initState = {
  core: {
    scan_from: null,
    location: {
      type: LocationType.NEAR_BY,
      lng: 0,
      lat: 0,
      radius_meters: 5200,
    },
    contactName: '',
    contactPhone: '',
    storeId: 0,
    shippingData: {
      addressLine: '62 Tố Hữu',
      addressType: '',
      city: '',
      country: '',
      district: '',
      fullAddress: 'Căn Shophouse SH12 The Manor Crown, 62 Tố Hữu, Phường Vỹ Dạ, TP Huế, Việt Nam',
      latitude: 0,
      longitude: 0,
      stateProvinceRegion: '',
      street: '',
      ward: '',
    },
    saleType: SaleType.Delivery,
    deliveryTime: getNextSlotDeliveryTime(dayjs(), 10, 30).format(),
    order_note: '',
    order_meta: {
      trainCode: '',
      coachCode: '',
      seatNumber: ''
    },
    invoice: {
      hasInvoice: false,
      taxCode: '',
      companyName: '',
      address: '',
      email: ''
    },
    voucher_codes: [],
  },
  order: {
    contactName: '',
    contactPhone: '',
    bankCode: '',
    storeId: null,
    shippingMethod: '',
    paymentMethod: '',
    saleType: SaleType.PickUp, // hoặc SaleType.DineIn | SaleType.PickUp nếu cần
    redeemPoint: 0,
    rankDiscount: 0,
    rankDiscountAmount: 0,
    source: 'web_qr',
    areaCode: '',
    menuProfileId: '',
    orderNote: '',
    returnUrl: '',
    deliveryTime: '',
    products: [],
    cartAmount: 0,
    brandCode: '',
    couponCodes: [],
    voucherCodes: [],
    discountCodes: [],
    shippingData: {
      addressLine: '',
      addressType: '',
      stateProvinceRegion: '',
      country: '',
      city: '',
      district: '',
      ward: '',
      street: '',
      longitude: 0,
      latitude: 0,
    },
    electronicInvoice: {
      isElectronicInvoice: false,
      companyName: '',
      taxCode: '',
      address: '',
      email: '',
    },
    orderMeta: {
      deviceId: '',
      deviceName: '',
      deviceOs: '',
      deviceIp: '',
      deviceToken: '',
    },
    promotionProductSelectedList: [],
    validateDisableProductMenuStore: [],
    // Áp dụng cho Web Order
    checkout: {
      order_id: ''
    },
    /**
     * tableId
     * Được gửi lên API tạo đơn hàng để lưu thông tin bàn
     */
    tableId: null,
  },
  checkout: {
    id: '',
    id_type: 'uuid'
  } as Store['checkout'],
}

export const useCart = create<Store>()(
  persist(
    (set, get) => ({
      core: initState.core,
      products: [],
      order: initState.order,
      checkout: initState.checkout,
      updateCore: (core: Partial<Store['core']>) => {
        set({
          core: {
            ...get().core,
            ...core,
          },
        })

        get().generateOrder()
      },
      formatProduct: (product: CartItem) => {
        const itemNo = product?.itemNo ? product?.itemNo : nanoid()

        return {
          ...product,
          itemNo,
          itemNote: normalizeItemNote(product.itemNote),
          itemQty: product.itemQty ?? 1,
        }
      },
      addProduct: (product: CartItem) => {
        if (!product?.id) throw new Error('You must provide an `id` for products')
        if (!product?.itemQty || product?.itemQty <= 0) throw new Error('Quantity must be greater than 0')

        const formattedProduct = get().formatProduct(product)
        const products = get().products
        const currentProduct = product.itemNo
          ? products.find((item) => item.itemNo === product.itemNo)
          : undefined

        // Editing updates the current cart line instead of creating a new one.
        if (currentProduct) {
          const sameConfigurationProduct = products.find((item) =>
            item.itemNo !== currentProduct.itemNo &&
            isSameCartItemConfiguration(item, formattedProduct)
          )

          // If the edited configuration already exists, merge the two lines.
          if (sameConfigurationProduct) {
            const mergedQuantity =
              (sameConfigurationProduct.itemQty ?? 0) + product.itemQty

            set({
              products: products
                .filter((item) => item.itemNo !== currentProduct.itemNo)
                .map((item) =>
                  item.itemNo === sameConfigurationProduct.itemNo
                    ? { ...item, itemQty: mergedQuantity }
                    : item
                ),
            })

            get().generateOrder()
            return
          }

          get().updateProduct({
            ...formattedProduct,
            itemNo: currentProduct.itemNo,
            itemQty: product.itemQty,
          })
          return
        }

        const existingProduct = products.find((item) =>
          isSameCartItemConfiguration(item, formattedProduct)
        )

        if (!existingProduct) {
          set({
            products: [formattedProduct, ...products],
          })

          get().generateOrder()
          return
        }

        // Same product, toppings, and note represent the same cart line.
        get().updateProduct({
          ...existingProduct,
          ...formattedProduct,
          itemNo: existingProduct.itemNo,
          itemQty: (existingProduct.itemQty ?? 0) + product.itemQty,
        })
      },
      updateProduct: (updatedProduct: CartItem) => {
        if (!updatedProduct?.itemNo) throw new Error('You must provide an `itemNo` for the product to update')
        if (updatedProduct?.itemQty === undefined || updatedProduct?.itemQty < 0) {
          throw new Error('Quantity must be 0 or greater')
        }

        const products = get().products
        const currentProduct = products.find(
          (item) => item.itemNo === updatedProduct.itemNo
        )

        if (!currentProduct) {
          console.warn(`Cart item with itemNo ${updatedProduct.itemNo} does not exist`)
          return
        }

        if (updatedProduct.itemQty === 0) {
          set({
            products: products.filter(
              (item) => item.itemNo !== updatedProduct.itemNo
            ),
          })

          get().generateOrder()

          return
        }

        set({
          products: products.map((item) =>
            item.itemNo === updatedProduct.itemNo
              ? { ...item, ...updatedProduct, itemNo: item.itemNo }
              : item
          ),
        })

        get().generateOrder()
      },
      deleteProduct: (itemNo: string) => {
        if (!itemNo) {
          console.warn('You must provide an `itemNo` to delete a product from the cart');
          return
        }

        const product = get().getProduct(itemNo)

        if (!product) {
          console.warn(`Product with itemNo ${itemNo} does not exist in the cart`);
          return
        }

        // Remove the product from the cart
        set({
          products: get().products.filter((item) => item.itemNo !== itemNo),
        })

        get().generateOrder()
      },
      // Get product by itemNo
      getProduct: (itemNo: string): CartItem | undefined => {
        const products = get().products;
        return products?.find((item) => item?.itemNo === itemNo);
      },
      setVoucherCode: (voucherCode: string) => {
        const { voucher_codes = [] } = get().core
        const voucherSelectMode = 'single' // Mặc định là 'single'

        let updated: string[]

        const isSelected = voucher_codes.includes(voucherCode)

        if (voucherSelectMode === 'single') {
          updated = isSelected ? [] : [voucherCode]
        } else {
          updated = isSelected
            ? voucher_codes.filter((c) => c !== voucherCode)
            : [...voucher_codes, voucherCode]
        }

        set({
          core: {
            ...get().core,
            voucher_codes: updated,
          },
        })

        get().generateOrder()
      },
      setPaymentMethod: (method: string) => {
        set({
          order: {
            ...get().order,
            paymentMethod: method,
          },
        })
      },
      updateOrderMeta: (payload: Partial<Store['core']['order_meta']>) => {
        set((state) => ({
          core: {
            ...state.core,
            order_meta: {
              ...state.core.order_meta,
              ...payload,
            },
          },
        }))

        get().generateOrder()
      },
      initCart: () => {
        const currentStoreId = get().core?.storeId
        const currentScanFrom = get().core?.scan_from
        const currentInvoice = get().core?.invoice

        const currentContactName = get().core?.contactName
        const currentContactPhone = get().core?.contactPhone

        const currentOrderMeta = get().core.order_meta

        set({
          core: {
            ...initState?.core,
            contactName: currentContactName,
            contactPhone: currentContactPhone,
            storeId: currentStoreId ?? 0,
            scan_from: currentScanFrom ?? null,
            invoice: currentInvoice,
            order_meta: currentOrderMeta
          },
          products: []
        })
      },
      generateOrder: () => {
        const config = getZmpConfig()
        const shippingData = () => {
          // Xóa shippingData với đơn PickUp
          if (get().core?.saleType === SaleType.PickUp) {
            return {
              addressLine: null,
              addressType: null,
              stateProvinceRegion: null,
              country: null,
              city: null,
              district: null,
              ward: null,
              street: null,
              longitude: 0,
              latitude: 0,
              fullAddress: null
            };
          }

          return {
            ...get().core?.shippingData,
            // addressType: "",
            // addressLine: "35 Nguyễn Huệ, Phường Bến Nghé, Quận 1, Hồ Chí Minh",
            // street: "",
            // city: "",
            // district: "",
            // ward: "",
            // country: "",
            // stateProvinceRegion: "",
            // longitude: 106.70537641799335,
            // latitude: 10.772276832320326,
            // fullAddress: "35 Nguyễn Huệ, Phường Bến Nghé, Quận 1, Hồ Chí Minh"
            fullAddress: get().core?.shippingData?.fullAddress,
          };
        }

        const products = get().products?.map((product) => {
          const formatOrderItemOptions = (itemOptions?: CartItemOptions) => {
            if (!itemOptions) return []; // Xử lý khi itemOptions là null hoặc undefined

            return Object.values(itemOptions).reduce((acc, items) => {
              items.forEach(({ id, quantity }) => {
                acc.push({
                  productOptionValueId: Number(id),
                  quantity: quantity ?? 1, // Đảm bảo quantity có giá trị
                });
              });
              return acc;
            }, [] as { productOptionValueId: number; quantity: number }[]);
          };

          const formatComboGroupItems = (comboGroups: ProductDto['combo_groups']) => {
            if (!comboGroups?.length) return []
            
            return comboGroups?.flatMap(({ data }) =>
              data?.map((combo_group_item) => ({
                comboGroupId: combo_group_item?.combo_group_id,
                comboGroupItemId: combo_group_item?.combo_group_item_id,
                productId: combo_group_item?.product?.id,
                quantity: 1,
                lineType: combo_group_item?.product?.type,
              }))
            )
          }

          return {
            productId: product?.id,
            quantity: product?.itemQty,
            orderItemOptions: formatOrderItemOptions(product?.itemOptions),
            comboGroupItems: formatComboGroupItems(product?.combo_groups),
            note: product?.itemNote,
            isActive: true,
            lineType: product?.line_type?.id
          }
        })

        const deliveryTime = () => {
          /**
           * Nếu là đơn Dine In, không cần deliveryTime
           */
          if (get().core?.scan_from === ScanForm.DINE_IN) {
            return ''
          }

          return dayjs(get().core?.deliveryTime).tz('Asia/Bangkok').format()
        }

        const electronicInvoice = () => {
          if (!get().core?.invoice?.hasInvoice) {
            return {
              isElectronicInvoice: false,
              companyName: '',
              taxCode: '',
              address: '',
              email: '',
            }
          }

          return {
            isElectronicInvoice: true,
            companyName: get().core?.invoice?.companyName ?? '',
            taxCode: get().core?.invoice?.taxCode ?? '',
            address: get().core?.invoice?.address ?? '',
            email: get().core?.invoice?.email ?? '',
          }
        }

        const tableNo = () => {
          if (get().core?.scan_from === ScanForm.DINE_IN) {
            return `${useStoreStore.getState().table_no?.formatted?.table ?? ''}`.trim()
          }
        }

        const orderNote = () => {
          const orderNote = get().core?.order_note ?? ''

          return orderNote
        }

        set({
          order: {
            ...initState.order,
            ...get().order,
            saleType: get().core?.saleType,
            contactName: get().core?.contactName,
            contactPhone: get().core?.contactPhone,
            shippingData: shippingData(),
            storeId: get().core?.storeId ?? null,
            menuProfileId: config.menuCode,
            products,
            paymentMethod: 'CoD',
            shippingMethod: '',
            deliveryTime: deliveryTime(),
            orderNote: orderNote(),
            electronicInvoice: electronicInvoice(),
            validateDisableProductMenuStore: [get().core?.storeId],
            discountCodes: get().core?.voucher_codes,
            returnUrl: createStorefrontUrl('/orders/result'),
            tableId: tableNo(),
            orderMeta: {
              deviceId: get().core?.order_meta?.deviceId ?? '',
              deviceName: get().core?.order_meta?.deviceName ?? '',
              deviceOs: get().core?.order_meta?.deviceOs ?? '',
              deviceIp: get().core?.order_meta?.deviceIp ?? '',
              deviceToken: get().core?.order_meta?.deviceToken ?? '',
              // Train-related meta (may be undefined)
              trainCode: get().core?.order_meta?.trainCode,
              coachCode: get().core?.order_meta?.coachCode,
              seatNumber: get().core?.order_meta?.seatNumber,
            }
          }
        })
      },
      // Áp dụng cho Web Order
      createCheckout: async (order) => {

        set({
          checkout: {
            id: order.id,
            id_type: "uuid"
          }
        })
      }
    }),
    {
      name: getStorefrontStorageKey('cart-storage'),
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
    },
  ),
)

export function getNextSlotDeliveryTime(date, step, minLeadTime = 10) {
  // Làm tròn lên mốc slot gần nhất
  const minute = date.minute()
  const rounded = Math.ceil(minute / step) * step
  let slot = date.minute(0).second(0).add(rounded, 'minute')

  // Nếu còn lại < minLeadTime, đẩy lên slot tiếp theo
  if (slot.diff(date, 'minute') < minLeadTime) {
    slot = slot.add(step, 'minute')
  }

  return slot
}
